import * as React from 'react';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from '../../../core/utils/classnames.tsx';
import { useAssistanceContext } from './AssistanceContext.tsx';
import { camelCase } from 'lodash';
import { getFormFieldGroups, GROUP_SEPARATOR, useDocumentConfig } from './useDocumentConfig.ts';
import ItemDataSection from './ItemDataSection.tsx';
import HeaderDataSection from './HeaderDataSection.tsx';
import SplitPanes, { Pane } from '../../../core/components/SplitPanes.tsx';
import { Trans, useTranslation } from 'react-i18next';
import Tooltip from '../../../core/components/Tooltip.tsx';
import IncompleteChecksBanner from './IncompleteChecksBanner.tsx';
import ManualReviewBanner from './ManualReviewBanner.tsx';
import { useFeatureToggle } from '../../../core/utils/hooks/useFeatureToggle.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import {
    faArrowUpRightFromSquare,
    faArrowUpToLine,
    faBadgePercent,
    faBarsFilter,
    faBox,
    faBuildings,
    faChevronDown,
    faCircleQuestion,
    faCommand,
    faCommentDollar,
    faEnvelopeOpenText,
    faFileInvoiceDollar,
    faFileLines,
    faListCheck,
    faListTree,
    faMicrophone,
    faShoppingCart,
    faSpinnerThird,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { AssistanceViewContextProvider } from './AssistanceViewContext.tsx';
import type { VirtualizedItemListHandle } from './VirtualizedItemList.tsx';
import { checkIsPdfEmbeddedSupported, filterFastTrack } from './utils.tsx';
import { useLocalStorage } from '../../../core/utils/hooks/useLocalStorage.ts';
import Button from '../../../core/components/Button.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import { useConditionalComponent } from '../../../customizations/ConditionalComponentContext.tsx';
import { Link, useNavigate } from 'react-router';
import { url } from '../../../core/utils/link.ts';
import { TABS_OPTIONS, useCurrentTabName } from './tabs.ts';
import { useSearchParams } from 'react-router-dom';
import { filterAttachments } from '../../utils.ts';
import SourceFileView from './components/SourceFileView.tsx';
import EmailsView, { EmailHeader } from './EmailsView.tsx';
import AssistanceImageMap from './AssistanceImageMap.tsx';
import FileInspectorModal from './components/FileInspectorModal.tsx';
import { EmailAvatar } from './components/EmailEditor.tsx';
import { isInstanceOfEmailResult, isInstanceOfFile } from './types.ts';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import { useMutation } from '@apollo/client';
import { CREATE_RECORD_FROM_EMAIL_PART, SWAP_SOURCE_DATA, UPDATE_SOURCE_PART_CLASSIFICATION } from '../../queries.ts';
import {
    CLASSIFICATION_OPTIONS,
    DocumentType,
    documentTypeToKey,
    mapDocumentTypeToGqlDocumentType,
} from '../../constants.ts';
import Radio from '../../../core/components/Radio.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import ResultView from './components/ResultView.tsx';
import VoiceConversationView from './VoiceConversationView.tsx';
import EInvoiceView from './EInvoiceView.tsx';
import InputDataPreview from './components/InputDataPreview.tsx';
import { MasterDataVersionSummary } from '../../../masterdata/components/MasterDataVersionSummary.tsx';
import { usePushNotifications } from '../../../core/utils/usePushNotifications.ts';
import AssistanceNotificationsCard from './components/AssistanceNotificationsCard.tsx';
import SelfServiceFormBanner from '../../components/SelfServiceFormBanner.tsx';
import { canManageSelfServiceForms } from '../../../users/utils.ts';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';

const EmailBodyIcon = withIcon(faEnvelopeOpenText);
const DocumentIcon = withIcon(faFileLines);
const LoadingIcon = withIcon(faSpinnerThird);
const FastTrackIcon = withIcon(faBarsFilter);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);
const FileIcon = withIcon(faFileLines);
const CloseIcon = withIcon(faXmark);
const MicrophoneIcon = withIcon(faMicrophone);
const ProcessIcon = withIcon(faArrowUpToLine);
const ChevronDownIcon = withIcon(faChevronDown);

// Document type icons
const RfqIcon = withIcon(faCommentDollar);
const OrderIcon = withIcon(faShoppingCart);
const OrderConfirmationIcon = withIcon(faListCheck);
const DeliveryNoteIcon = withIcon(faBox);
const InvoiceIcon = withIcon(faFileInvoiceDollar);
const PropertyBillIcon = withIcon(faBuildings);
const ListOfServicesIcon = withIcon(faListTree);
const UniversalIcon = withIcon(faCommand);
const OfferIcon = withIcon(faBadgePercent);
const OtherIcon = withIcon(faCircleQuestion);

// Map classification labels to icons
const getDocumentTypeIcon = (classificationLabel: string) => {
    if (!classificationLabel) return null;

    const label = classificationLabel.toLowerCase();

    if (label.includes('rfq') || label.includes('request')) return RfqIcon;
    if (label.includes('order_confirmation') || label.includes('order confirmation')) return OrderConfirmationIcon;
    if (label.includes('order')) return OrderIcon;
    if (label.includes('delivery') || label.includes('note')) return DeliveryNoteIcon;
    if (label.includes('invoice')) return InvoiceIcon;
    if (label.includes('property') || label.includes('bill')) return PropertyBillIcon;
    if (label.includes('service')) return ListOfServicesIcon;
    if (label.includes('universal')) return UniversalIcon;
    if (label.includes('offer')) return OfferIcon;
    if (label.includes('other')) return OtherIcon;

    return null;
};

const DEFAULT_THRESHOLD = 90;

type DeleteReasonType = 'invalid document type' | 'other_reason';

type ProcessModalProps = {
    visible: boolean;
    title: string;
    labelConfirm: string;
    labelCancel: string;
    documentType: DocumentType;
    onCancel: () => void;
    onConfirm: (deleteSourceRecord: boolean, deleteReason: string | undefined) => void;
    children: ReactNode;
};

const ProcessModal: FC<ProcessModalProps> = ({
    visible,
    title,
    labelConfirm,
    labelCancel,
    documentType,
    onCancel,
    onConfirm,
    children,
}) => {
    const { t } = useTranslation('assistance');
    const [deleteSourceRecord, setDeleteSourceRecord] = useState(true);
    const [reasonType, setReasonType] = useState<DeleteReasonType>('other_reason');
    const [otherReasonText, setOtherReasonText] = useState('');

    useEffect(() => {
        if (!visible) {
            setDeleteSourceRecord(true);
            setReasonType('other_reason');
            setOtherReasonText('');
        }
    }, [visible]);

    const handleConfirm = () => {
        const reason = reasonType === 'invalid document type' ? reasonType : otherReasonText;
        onConfirm(deleteSourceRecord, deleteSourceRecord ? reason || undefined : undefined);
    };

    const radioOptions: { value: DeleteReasonType; label: string }[] = [
        {
            value: 'invalid document type',
            label: t(`reprocessModal.wrongDocumentType.${documentTypeToKey[documentType]}`),
        },
        { value: 'other_reason', label: t('deleteModal.otherReason') },
    ];

    return (
        <ConfirmModal
            title={title}
            labelConfirm={labelConfirm}
            labelCancel={labelCancel}
            visible={visible}
            onCancel={onCancel}
            onConfirm={handleConfirm}
        >
            <div className="text-content-strong font-normal text-sm">
                {children}

                <label className="flex mt-4 gap-2 items-center group hover:bg-surface-medium rounded transition-colors focus-within:bg-surface-medium cursor-pointer">
                    <Checkbox checked={deleteSourceRecord} onChange={(e) => setDeleteSourceRecord(e.target.checked)} />
                    <span className="text-sm text-content-strong">{t('reprocessModal.deleteLabel')}</span>
                </label>

                {deleteSourceRecord && (
                    <div className="mt-4 flex flex-col gap-3">
                        <span className="text-content-strong font-normal text-sm">
                            {t('reprocessModal.reasonTitle')}
                        </span>
                        <Radio.Group
                            value={reasonType}
                            onValueChange={(value) => setReasonType(value as DeleteReasonType)}
                            className="gap-3"
                        >
                            {radioOptions.map(({ value, label }) => (
                                <div key={value} className="flex flex-col gap-2">
                                    <label className="group flex items-center gap-2 cursor-pointer">
                                        <Radio value={value} />
                                        <span className="text-content-strong text-sm">{label}</span>
                                    </label>
                                    {value === 'other_reason' && reasonType === 'other_reason' && (
                                        <FormField>
                                            <TextField
                                                value={otherReasonText}
                                                onValueChange={(v: string) => setOtherReasonText(v)}
                                                placeholder={t('reprocessModal.otherReasonPlaceholder')}
                                            />
                                        </FormField>
                                    )}
                                </div>
                            ))}
                        </Radio.Group>
                    </div>
                )}
            </div>
        </ConfirmModal>
    );
};

const SidebarControls = ({ isFastTrackEnabled, onFastTrackEnabledChange, fastTrackCount = 0 }: any) => {
    const { t } = useTranslation('assistance');
    const { handlers, loading, record, readOnly } = useAssistanceContext();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fastTrackTooltipOpen, setFastTrackTooltipOpen] = useState(isFastTrackEnabled);
    const isERPLockEnabled = useFeatureToggle('ERPLock', { channel: record?.channel });
    const isERPLocked = record?.orderUnsaved?.isErpLocked ?? false;

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await handlers?.onFinishAndContinue();
        } catch (error) {
            // only reset on error as we do a redirect on success
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (!isFastTrackEnabled) {
            setFastTrackTooltipOpen(false);
            return;
        }
        setFastTrackTooltipOpen(true);
        const timer = setTimeout(() => setFastTrackTooltipOpen(false), 4000);
        return () => clearTimeout(timer);
    }, [isFastTrackEnabled]);

    const canFinishAssistance = record?.canFinishAssistance && !isSubmitting;

    if (!isERPLockEnabled && readOnly) return null;

    return (
        <div className="flex flex-col gap-4 flex-none overflow-hidden p-6 border-t border-solid border-line-weak field-modal-root">
            {!readOnly && (
                <div className="flex gap-2">
                    <Tooltip
                        content={
                            isFastTrackEnabled
                                ? t('finishButton.fastTrackTooltipOn')
                                : t('finishButton.fastTrackTooltip')
                        }
                        side="top"
                        open={fastTrackTooltipOpen}
                        onOpenChange={setFastTrackTooltipOpen}
                    >
                        <Button
                            onClick={() => onFastTrackEnabledChange(!isFastTrackEnabled)}
                            className="flex gap-2 items-center"
                            active={isFastTrackEnabled}
                            disabled={readOnly}
                        >
                            <FastTrackIcon /> {fastTrackCount}
                        </Button>
                    </Tooltip>

                    <Tooltip
                        content={
                            loading || !canFinishAssistance
                                ? t('finishButton.finishDisabledNote')
                                : t('finishButton.finishNote')
                        }
                        side="top"
                        long
                        className="tooltip--flex"
                        open={readOnly ? false : undefined}
                    >
                        <Button
                            variant="primary"
                            className="flex items-center justify-center w-full p-2 !bg-surface-brand-strong-default rounded-md text-content-white dark:text-content-strong font-medium text-sm hover:enabled:!bg-surface-brand-strong-hover transition-colors duration-200 focus:enabled:!bg-surface-brand-strong-focus active:enabled:!bg-surface-brand-strong-active disabled:opacity-50"
                            disabled={loading || readOnly || !canFinishAssistance}
                            onClick={handleSubmit}
                        >
                            {t('finishButton.finishAndContinueButton')}
                        </Button>
                    </Tooltip>
                </div>
            )}

            {isERPLockEnabled && (
                <div className="flex justify-center">
                    <label className={classnames('flex gap-2', !readOnly && 'cursor-pointer')}>
                        {loading ? (
                            <LoadingIcon spin className="text-content-brand-strong" />
                        ) : (
                            <Checkbox
                                checked={isERPLocked}
                                disabled={readOnly}
                                onChange={() =>
                                    handlers?.onUpdate({
                                        fieldName: 'isErpLocked',
                                        payload: { value: !isERPLocked },
                                    })
                                }
                            />
                        )}
                        <span className="text-sm">{t(`fields.ERPLock.${readOnly ? 'read' : 'edit'}`)}</span>
                    </label>
                </div>
            )}
        </div>
    );
};

const AssistanceSidebar = ({ className, children }: any) => {
    return (
        <aside className={classnames('flex flex-col flex-auto min-h-0 h-full overflow-y-auto', className)}>
            {children}
        </aside>
    );
};

const AttachmentLabel = ({
    isSource,
    file,
    sourcePart,
    onSelectedFileToSwapChange,
    onProcessDocumentSelect,
    onGoToDocumentClick,
}: {
    isSource: boolean;
    file: any;
    sourcePart?: any;
    onSelectedFileToSwapChange?: (file: any) => void;
    onProcessDocumentSelect: (file: any) => void;
    onGoToDocumentClick: (recordId: string) => void;
}) => {
    const { t } = useTranslation('assistance');

    if (isSource) {
        return null;
    }

    // If there's a sourcePart with a recordId, show "Go to document"
    if (sourcePart?.recordId) {
        return (
            <span
                onClick={() => onGoToDocumentClick(sourcePart.recordId)}
                className="ml-auto px-2 h-6 leading-6 hover:bg-surface-medium text-content-strong text-xs font-medium cursor-pointer hover:bg-surface-strong"
            >
                <ExternalLinkIcon /> {t('attachments.goToDocument')}
            </span>
        );
    }

    // If there's a sourcePart but no recordId, show "Process document"
    if (sourcePart) {
        return (
            <span
                onClick={() => onProcessDocumentSelect(file)}
                className="ml-auto px-2 h-6 leading-6 hover:bg-surface-medium text-content-strong text-xs font-medium cursor-pointer hover:bg-surface-strong"
            >
                <ProcessIcon /> {t('attachments.processDocument')}
            </span>
        );
    }

    // No sourcePart - don't show any button
    return null;
};

const AssistanceSidebarAttachment = React.memo(
    ({
        file,
        activeAttachment,
        onActiveAttachmentChange,
        isSource,
        sourcePart,
        onSelectedFileToSwapChange,
        onProcessDocumentSelect,
        onGoToDocumentClick,
        docConfig,
        channelDocumentConfig,
        upfrontClassification,
        onClassificationChange,
        isClassificationUpdating,
    }: any) => {
        const { t } = useTranslation('assistance');

        const pdfSupported = checkIsPdfEmbeddedSupported();

        const AttachmentLabelComponent = useConditionalComponent(
            'AttachmentLabel',
            {
                onSelectedFileToSwapChange: onSelectedFileToSwapChange,
                onProcessDocumentSelect: onProcessDocumentSelect,
                onGoToDocumentClick: onGoToDocumentClick,
                upfrontClassification,
            },
            AttachmentLabel
        );

        // Determine if classification confidence is below threshold
        const confidenceLevels = {
            STRICT: 95,
            BALANCED: 90,
            LIGHT: 80,
        };
        const sensitivity = channelDocumentConfig?.documentClassificationSensitivity;
        const threshold = sensitivity ? confidenceLevels[sensitivity] : DEFAULT_THRESHOLD;
        const isLowConfidence = sourcePart?.classificationConfidence * 100 < threshold;

        // Get the document type icon
        const DocumentTypeIcon = sourcePart?.classificationLabel
            ? getDocumentTypeIcon(sourcePart.classificationLabel)
            : null;

        const currentLabel = sourcePart?.classificationLabel?.toLowerCase();

        return (
            <div
                className={classnames(
                    'border border-line-weak rounded-xl bg-surface-weak shadow-sm hover:shadow-md transition overflow-hidden',
                    isSource
                        ? 'border-line-brand-active hover:border-line-brand-focus'
                        : 'border-line-weak hover:border-line-hover'
                )}
            >
                <div className={classnames('flex w-full text-left items-center p-3 gap-2')}>
                    <button
                        onClick={() => onActiveAttachmentChange(file)}
                        className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                    >
                        <FileIcon className={classnames('w-2', isSource ? 'text-content-brand-strong' : '')} />

                        <span
                            className={classnames(
                                'text-content-black text-xs font-medium truncate flex-1 text-left',
                                isSource ? 'text-content-brand-strong' : ''
                            )}
                        >
                            {file.name}
                        </span>
                    </button>
                    {sourcePart?.classificationLabel && (
                        <DropdownMenu>
                            <DropdownMenu.Trigger asChild disabled={isClassificationUpdating}>
                                <button
                                    className={classnames(
                                        'flex items-center gap-1.5 text-xs px-2 py-1 rounded cursor-pointer flex-none outline-none border border-transparent aria-expanded:bg-surface-brand-weak aria-expanded:border-line-brand-weak',
                                        isLowConfidence
                                            ? 'text-content-warning bg-surface-warning-weak'
                                            : 'bg-surface-strong-default',
                                        isClassificationUpdating && 'opacity-70 pointer-events-none'
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {isClassificationUpdating ? (
                                        <LoadingIcon spin className="w-3 h-3" />
                                    ) : (
                                        DocumentTypeIcon && <DocumentTypeIcon className="w-3 h-3" />
                                    )}
                                    {t(`attachments.classification.${currentLabel}`)}
                                    <ChevronDownIcon className="w-2.5 h-2.5" />
                                </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content align="end" className="w-[20rem]">
                                <DropdownMenu.Label className="text-content-default text-sm">
                                    {t('attachments.changeClassification')}
                                </DropdownMenu.Label>
                                <DropdownMenu.RadioGroup
                                    value={currentLabel}
                                    onValueChange={(value) => {
                                        if (value !== currentLabel && sourcePart?.id) {
                                            onClassificationChange?.(
                                                sourcePart.id,
                                                value.toUpperCase(),
                                                currentLabel?.toUpperCase()
                                            );
                                        }
                                    }}
                                >
                                    {CLASSIFICATION_OPTIONS.map((option) => {
                                        const OptionIcon = getDocumentTypeIcon(option);
                                        const isSelected = option === currentLabel;
                                        return (
                                            <DropdownMenu.RadioItem
                                                className="cursor-pointer"
                                                key={option}
                                                value={option}
                                            >
                                                <span className="flex items-center gap-2">
                                                    {OptionIcon && <OptionIcon className="w-3.5 h-3.5" />}
                                                    {t(`attachments.classification.${option}`)}
                                                </span>
                                                {isSelected && sourcePart?.classificationConfidence != null && (
                                                    <span className="ml-auto mr-6 text-sm text-content-brand-medium border-line-brand-strong bg-surface-brand-weak border rounded-sm px-1">
                                                        {!sourcePart.classificationCorrectedBy ||
                                                        !sourcePart.classificationCorrectedAt
                                                            ? `${(sourcePart.classificationConfidence * 100).toFixed(2)}%`
                                                            : t('attachments.edited')}
                                                    </span>
                                                )}
                                            </DropdownMenu.RadioItem>
                                        );
                                    })}
                                </DropdownMenu.RadioGroup>
                            </DropdownMenu.Content>
                        </DropdownMenu>
                    )}
                </div>
                <div className="cursor-pointer" onClick={() => onActiveAttachmentChange(file)}>
                    {file.type.startsWith('image/') ? (
                        <div className="relative w-full min-h-[9.5rem] flex justify-center items-center overflow-hidden pointer-events-none">
                            <img src={file.url} alt={file.name} className="max-w-full max-h-full object-contain" />
                            <div className="absolute bottom-0 w-full h-[3.125rem]"></div>
                        </div>
                    ) : file.type === 'application/pdf' && pdfSupported ? (
                        <div
                            className={classnames('min-h-[9.5rem] overflow-hidden max-h-[9.5rem] pointer-events-none')}
                        >
                            {/* Since BE always returns different url with different query params, it can blink during the Pending state loads */}
                            <iframe
                                src={file.url}
                                className={classnames('w-full h-full scale-[1.1]')}
                                title={file.name}
                            ></iframe>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center px-12 min-h-[9.5rem] max-h-[9.5rem] bg-surface-medium pointer-events-none">
                            <div className="text-sm font-semibold">{t('attachments.absentFileTitle')}</div>
                            <div className="mt-2 text-sm text-content-strong text-center">
                                {t('attachments.absentFileDescription')}
                            </div>
                        </div>
                    )}
                </div>

                <button className="flex w-full text-left items-center border-t border-line-weak p-3 gap-2">
                    <AttachmentLabelComponent
                        isSource={isSource}
                        file={file}
                        sourcePart={sourcePart}
                        onSelectedFileToSwapChange={onSelectedFileToSwapChange}
                        onProcessDocumentSelect={onProcessDocumentSelect}
                        onGoToDocumentClick={onGoToDocumentClick}
                    />
                </button>
            </div>
        );
    }
);

export const AssistanceSidebarAttachments = ({
    attachments,
    onClose,
}: {
    attachments: { isSource: boolean; file: any }[];
    onClose: () => void;
}) => {
    const { t } = useTranslation('assistance');

    const { record, documentConfiguration: docConfig, refetchData } = useAssistanceContext();
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const [activeAttachment, setActiveAttachment] = useState(null);

    const [selectedFileToSwap, setSelectedFileToSwap] = useState(null);
    const [selectedFileToProcess, setSelectedFileToProcess] = useState(null);
    const [swapSourceData] = useMutation(SWAP_SOURCE_DATA);
    const [updateClassification] = useMutation(UPDATE_SOURCE_PART_CLASSIFICATION);

    const [createRecordFromEmailPart] = useMutation(CREATE_RECORD_FROM_EMAIL_PART);

    const channelDocumentConfig = useMemo(() => {
        const options = record?.channel?.documentConfig?.options || [];
        return Object.fromEntries(options.map((option: any) => [camelCase(option.name), option.value]));
    }, [record?.channel?.documentConfig?.options]);

    const attachmentsWithSourceParts = useMemo(() => {
        const sourceParts = record?.sourceParts || [];
        return attachments
            .map(({ isSource, file }) => {
                const sourcePart = file.sourcePartId
                    ? sourceParts.find((sp) => sp.id === file.sourcePartId)
                    : sourceParts.find((sp) => sp.fileName === file.name);
                return { isSource, file, sourcePart };
            })
            .filter(({ sourcePart }) => sourcePart?.partType !== 'BODY'); // Filter out email body
    }, [attachments, record?.sourceParts]);

    // Find the email body sourcePart
    const emailBodySourcePart = useMemo(() => {
        const sourceParts = record?.sourceParts || [];
        return sourceParts.find((sp) => sp.partType === 'BODY');
    }, [record?.sourceParts]);

    // Check if email body is the source
    const isEmailBodySource = useMemo(() => {
        if (!emailBodySourcePart) return false;
        return record?.inputFile?.name === emailBodySourcePart.fileName;
    }, [emailBodySourcePart, record?.inputFile?.name]);

    const handleSwapSourceData = async () => {
        if (!selectedFileToSwap || !record) {
            return;
        }

        try {
            await swapSourceData({
                variables: {
                    recordId: record.id,
                    documentType: mapDocumentTypeToGqlDocumentType[docConfig.documentType],
                    targetFile: {
                        name: selectedFileToSwap?.name,
                        type: selectedFileToSwap?.type,
                        cid: selectedFileToSwap?.cid,
                        contentDisposition: selectedFileToSwap?.contentDisposition,
                    },
                },
                fetchPolicy: 'no-cache',
            });

            // Navigate to the document assistance tab after successful swap
            navigate(
                url(
                    docConfig.ASSISTANCE_TAB_PATH,
                    { recordId: record.id, tab: 'document' },
                    { keepSearch: true, excludeSearch: ['mode'] }
                )
            );
            await refetchData();
        } catch (error) {
            publishToast({
                description: t('swapSourceDataModal.errorMessage'),
                status: 'error',
            });
        } finally {
            setSelectedFileToSwap(null);
        }
    };

    const handleProcessDocument = async (deleteSourceRecord: boolean, deleteReason: string | undefined) => {
        if (!selectedFileToProcess || !record) {
            return;
        }

        // Find the sourcePart for the selected file
        const sourcePart = attachmentsWithSourceParts.find((a) => a.file === selectedFileToProcess)?.sourcePart;

        if (!sourcePart?.id) {
            publishToast({
                description: t('processDocumentModal.errorMessage'),
                status: 'error',
            });
            setSelectedFileToProcess(null);
            return;
        }

        try {
            const result = await createRecordFromEmailPart({
                variables: {
                    documentType: mapDocumentTypeToGqlDocumentType[docConfig.documentType],
                    recordId: record.id,
                    emailPartId: sourcePart.id,
                    deleteSourceRecord,
                    deleteReason,
                },
                fetchPolicy: 'no-cache',
            });

            const newRecordId = result.data?.createRecordFromEmailPart?.recordId;

            if (newRecordId) {
                navigate(
                    url(
                        docConfig.ASSISTANCE_TAB_PATH,
                        { recordId: newRecordId, tab: 'document' },
                        { keepSearch: false }
                    )
                );
            } else {
                console.error('Failed to extract record ID. Response data:', result.data);
            }

            await refetchData();

            publishToast({
                description: t('processDocumentModal.successMessage'),
                status: 'success',
            });
        } catch (error) {
            console.error('Error processing document:', error);
            publishToast({
                description: t('processDocumentModal.errorMessage'),
                status: 'error',
            });
        } finally {
            setSelectedFileToProcess(null);
        }
    };

    const handleGoToDocumentClick = (recordId: string) => {
        const targetUrl = url(docConfig.ASSISTANCE_TAB_PATH, { recordId, tab: 'document' }, { keepSearch: false });

        window.open(targetUrl, '_blank', 'noopener,noreferrer');
    };

    const [classificationUpdatingId, setClassificationUpdatingId] = useState<string | null>(null);

    const handleClassificationChange = async (
        sourcePartId: string,
        classificationLabel: string,
        previousLabel?: string
    ) => {
        setClassificationUpdatingId(sourcePartId);
        try {
            await updateClassification({
                variables: { sourcePartId, classificationLabel },
                fetchPolicy: 'no-cache',
            });
            await refetchData();
            publishToast({
                description: (
                    <span className="flex items-center gap-3">
                        <span>
                            {t('attachments.changeClassificationSuccess', {
                                documentType: t(`attachments.classification.${classificationLabel.toLowerCase()}`),
                            })}
                        </span>
                        {previousLabel && (
                            <button
                                className="border cursor-pointer border-line-strong rounded-sm p-1 px-2 font-semibold hover:opacity-80 flex-none"
                                onClick={() => handleClassificationChange(sourcePartId, previousLabel)}
                            >
                                {t('attachments.undo')}
                            </button>
                        )}
                    </span>
                ),
                status: 'success',
            });
        } catch (error) {
            publishToast({
                description: t('attachments.changeClassificationError'),
                status: 'error',
            });
        } finally {
            setClassificationUpdatingId(null);
        }
    };

    return (
        <AssistanceSidebar>
            <div
                className={classnames(
                    'flex justify-between items-center gap-8 px-6 border-b border-solid border-line-weak flex-none min-h-[3.5rem]'
                )}
            >
                <div className="text-base font-semibold text-sm text-content-strong max-w-[32rem]">
                    {t('attachments.files')}
                </div>

                <div className="w-8 h-8">
                    <Button
                        variant="ghost"
                        tabIndex={-1} // unfocusable (will autofocus the first child input instead)
                        className="w-8 h-8 -mr-2 p-0 flex items-center justify-center text-content-strong"
                        onClick={() => onClose()}
                    >
                        <CloseIcon className="w-5 h-5" />
                    </Button>
                </div>
            </div>
            <div className="p-4 space-y-4">
                {attachmentsWithSourceParts.map(({ isSource, file, sourcePart }, index) => (
                    <AssistanceSidebarAttachment
                        key={index}
                        file={file}
                        isSource={isSource}
                        sourcePart={sourcePart}
                        activeAttachment={activeAttachment}
                        onActiveAttachmentChange={setActiveAttachment}
                        onSelectedFileToSwapChange={setSelectedFileToSwap}
                        onProcessDocumentSelect={setSelectedFileToProcess}
                        onGoToDocumentClick={handleGoToDocumentClick}
                        docConfig={docConfig}
                        channelDocumentConfig={channelDocumentConfig}
                        upfrontClassification={record?.channel?.emailConfig?.upfrontClassification}
                        onClassificationChange={handleClassificationChange}
                        isClassificationUpdating={classificationUpdatingId === sourcePart?.id}
                    />
                ))}
            </div>
            <FileInspectorModal
                initialFile={activeAttachment}
                onClose={() => setActiveAttachment(null)}
                files={attachments?.map((a) => a.file)}
                attachmentsWithSourceParts={attachmentsWithSourceParts}
                onProcessDocumentSelect={setSelectedFileToProcess}
                onGoToDocumentClick={handleGoToDocumentClick}
            />
            <ConfirmModal
                title={t('swapSourceDataModal.title')}
                labelConfirm={t('swapSourceDataModal.confirmButton')}
                labelCancel={t('swapSourceDataModal.cancelButton')}
                visible={!!selectedFileToSwap}
                onCancel={() => setSelectedFileToSwap(null)}
                onConfirm={handleSwapSourceData}
            >
                <div className="text-gray-600">
                    <Trans
                        i18nKey="swapSourceDataModal.message"
                        t={t}
                        values={{
                            currentSourceFileName: record?.inputFile?.name,
                            newFileName: selectedFileToSwap?.name,
                        }}
                        components={{ bold: <strong />, br: <br /> }}
                    />
                </div>
            </ConfirmModal>
            <ProcessModal
                title={t('processDocumentModal.title')}
                labelConfirm={t('processDocumentModal.confirmButton')}
                labelCancel={t('processDocumentModal.cancelButton')}
                documentType={docConfig.documentType}
                visible={!!selectedFileToProcess}
                onCancel={() => setSelectedFileToProcess(null)}
                onConfirm={handleProcessDocument}
            >
                <Trans
                    i18nKey="processDocumentModal.message"
                    t={t}
                    values={{ fileName: selectedFileToProcess?.name }}
                    components={{ bold: <strong />, br: <br /> }}
                />
            </ProcessModal>
        </AssistanceSidebar>
    );
};

const AssistanceSidebarEmail = ({
    onClose,
    containerRef,
}: {
    onClose: () => void;
    containerRef?: React.RefObject<HTMLDivElement>;
}) => {
    const { t } = useTranslation('assistance');

    return (
        <AssistanceSidebar>
            <div className="flex justify-between items-center gap-8 px-4 border-b border-solid border-line-weak flex-none min-h-[3.5rem]">
                <h3 className="text-sm font-medium">{t('emailsView.title')}</h3>
                <Button
                    variant="ghost"
                    onClick={onClose}
                    className="w-8 h-8 -mr-2 p-0 flex items-center justify-center text-content-strong"
                >
                    <CloseIcon className="w-4 h-4" />
                </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
                <EmailsView containerRef={containerRef} />
            </div>
        </AssistanceSidebar>
    );
};

export const AssistanceSidebarProcessingHistory = ({ record, documentConfig, handlers, onClose }: any) => {
    const { t } = useTranslation('assistance');

    const outputFile = record?.outputFile;
    const stepRun = record?.stepRun || {};
    const isFinished = stepRun?.executionStatus === 'SUCCEEDED';
    const stepRunList = record?.[documentConfig?.documentTypeName.toLowerCase() + 'processingsteprunSet'] || [];

    const fileForResult = outputFile ? outputFile : isFinished ? document : undefined;
    const exportFile = fileForResult?.files?.[0];

    return (
        <AssistanceSidebar>
            <div className="flex justify-between items-center gap-8 px-4 border-b border-solid border-line-weak flex-none min-h-[3.5rem]">
                <h2 className="text-base text-sm font-medium text-content-strong">{t('resultModal.title')}</h2>
                <Button
                    variant="ghost"
                    onClick={onClose}
                    className="w-8 h-8 -mr-2 p-0 flex items-center justify-center text-content-strong"
                >
                    <CloseIcon className="w-4 h-4" />
                </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
                <ResultView record={record} stepRunList={stepRunList} onRetryStep={handlers.onRetryStep} />
            </div>

            <div className="flex flex-col gap-4 flex-none overflow-hidden pt-4 field-modal-root">
                {exportFile && (
                    <div className="flex gap-2 border-t p-4 border-solid border-line-weak">
                        <Button
                            asChild
                            className="flex items-center justify-center cursor-pointer w-full p-2 rounded-md text-sm  disabled:opacity-50"
                        >
                            <a href={exportFile?.url} target="_blank">
                                {t('resultView.downloadButton')}
                            </a>
                        </Button>
                    </div>
                )}
            </div>
        </AssistanceSidebar>
    );
};

export const AssistanceSidebarMasterData = ({ record, onClose }: any) => {
    const { t } = useTranslation('masterdata');
    return (
        <AssistanceSidebar>
            <div className="flex justify-between items-center gap-8 px-4 border-b border-solid border-line-weak flex-none min-h-[3.5rem]">
                <h3 className="font-medium text-sm text-content-strong">{t('versionSummary.title')}</h3>
                <Button
                    variant="ghost"
                    onClick={onClose}
                    className="w-8 h-8 -mr-2 p-0 flex items-center justify-center text-content-strong"
                >
                    <CloseIcon className="w-4 h-4" />
                </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
                <MasterDataVersionSummary versionInfo={record?.masterdataVersionsExtracted} />
            </div>
        </AssistanceSidebar>
    );
};

const DocumentView = React.memo(
    ({
        viewMode,
        prevViewMode,
        mdSidePanelWidth,
    }: {
        viewMode?: TABS_OPTIONS | undefined;
        prevViewMode?: TABS_OPTIONS | undefined;
        mdSidePanelWidth?: number | null;
    }) => {
        const { user } = useApplicationContext();
        const { handlers, record, document, documentConfiguration, readOnly, ocr, emailOcr } = useAssistanceContext();

        const { isSupported, isSubscribed, subscribe, unsubscribe } = usePushNotifications({
            recordId: record?.id,
            documentType: mapDocumentTypeToGqlDocumentType[documentConfiguration.documentType],
        });

        const { onUpdate, onReselect, onRetryStep } = handlers;

        const { t } = useTranslation('assistance');

        const navigate = useNavigate();
        const [searchParams] = useSearchParams();

        const areAttachmentsVisible = searchParams.get('mode') === 'attachments';

        const [_isFastTrackEnabled, setIsFastTrackEnabled] = useLocalStorage('assistanceFastTrackEnabled', false);
        const isFastTrackEnabled = !readOnly && _isFastTrackEnabled;

        // Field interaction state (focus/hover/update/reselect) lives in the interaction
        // store, written by AssistanceViewContextProvider; components subscribe to their slice.

        // image map

        const formsRef = useRef(null);
        const previewRef = useRef(null);
        const itemVirtualizerRef = useRef<VirtualizedItemListHandle>(null);
        const animationContainerRef = useRef<HTMLDivElement>(null);

        // measured via callback ref: DocumentView no longer re-renders on interaction, so a
        // render-time `.current` read would stay stale (0) until an unrelated re-render
        const [emailHeaderHeight, setEmailHeaderHeight] = useState(0);
        const handleEmailHeaderRef = useCallback((node: HTMLDivElement | null) => {
            setEmailHeaderHeight(node ? node.getBoundingClientRect().height : 0);
        }, []);

        // process configs for displaying the sections

        const configuredItemTypes = (record?.channel?.documentConfig?.fields || [])
            .filter((field) => field.options?.some((option) => option.name === 'group' && option.value === true))
            .map((field) => field.name);
        const resultItemTypes = configuredItemTypes.length > 0 ? configuredItemTypes : undefined;
        const hardcodedItemTypes = documentConfiguration?.itemTypes;
        const useDefaultItemTypes = documentConfiguration?.useDefaultItemTypes ?? true;
        const defaultItemTypes = useDefaultItemTypes ? ['line_items'] : undefined;
        const itemTypes = hardcodedItemTypes || resultItemTypes || defaultItemTypes;

        const { generalConfig, fieldConfigsByPrefix } = useDocumentConfig(record?.channel?.documentConfig, itemTypes);
        const { '': headerConfigs, ...itemConfigs } = fieldConfigsByPrefix;

        const headerFormFieldGroups = useMemo(() => getFormFieldGroups(document, headerConfigs), [document]);

        const [itemFormFieldGroups, items] = useMemo(() => {
            const items = [];
            const result = Object.fromEntries(
                Object.entries(itemConfigs).map(([prefix, fields]) => {
                    if (prefix.includes(GROUP_SEPARATOR)) {
                        prefix = prefix.split(GROUP_SEPARATOR).at(-1);
                    }

                    const sectionItems = document?.[camelCase(prefix)] || [];
                    items.push(...sectionItems);

                    let parentItemIndex = 0;
                    let subLineItemIndex = 0;

                    return [
                        prefix,
                        sectionItems.map((item, itemIndex) => {
                            let displayIndex;
                            const isSubLineItem = Boolean(item?.parentLineItemId);
                            if (isSubLineItem) {
                                subLineItemIndex += 1;
                                displayIndex = `${parentItemIndex}.${subLineItemIndex}`;
                            } else {
                                parentItemIndex += 1;
                                subLineItemIndex = 0;
                                displayIndex = `${parentItemIndex}`;
                            }

                            return getFormFieldGroups(item, fields, prefix, itemIndex, displayIndex).flat();
                        }),
                    ];
                })
            );
            return [result, items];
        }, [document]);

        const imageMapFields = useMemo(
            () =>
                [
                    ...headerFormFieldGroups.map((fieldGroup) => fieldGroup?.[0]),
                    ...Object.values(itemFormFieldGroups).flatMap((fields) => fields),
                ].flat(),
            [headerFormFieldGroups, itemFormFieldGroups]
        );

        const fastTrackCount = useMemo(() => {
            const [_, { medium, low }] = filterFastTrack([
                ...headerFormFieldGroups,
                ...Object.values(itemFormFieldGroups).flatMap((fields) => fields),
            ]);
            return low + medium;
        }, [headerFormFieldGroups, itemFormFieldGroups]);

        const SidebarControlsComponent = useConditionalComponent(
            'SidebarControls',
            {
                documentTypeName: documentConfiguration?.documentTypeName,
            },
            SidebarControls
        );

        const documentPreviewNotAvailable = !record?.dusDocument?.processedFileUrl && !record?.voiceAgentRecord?.id;
        const isPending = record?.status === 'PENDING' && !record?.dusDocument?.processedFileUrl;
        const isDocumentLoading = !record || isPending;

        const sourceFile = record?.sourceFile;
        const inputFile = record?.inputFile;
        const isInputFileEmail = isInstanceOfFile(inputFile) && inputFile.type === 'message/rfc822';

        /*
    If we have a sourceFile.name it means the document was uploaded directly to the WB and we display name of the file
    Otherwise if we have at least on attachment we display the inputFile.name as tab title
    otherwise we display the subject of the mail
    */
        const documentTabFileName =
            sourceFile?.name ??
            (sourceFile?.attachments?.length >= 1 ? record?.inputFile?.name : sourceFile?.subject || record?.subject);
        const documentTabFileUrl = record?.sourceFile?.url ?? record?.sourceFile?.sourceUrl;

        const eInvoice = record?.eInvoice;
        // The e-invoice's own file: an email attachment lives on inputFile, a direct upload on sourceFile.
        const findEInvoiceFile = (mimeMatches: (type: string) => boolean) =>
            eInvoice
                ? [inputFile, sourceFile].find(
                      (file) => isInstanceOfFile(file) && file.url && mimeMatches(file.type || '')
                  )
                : undefined;
        const eInvoiceXmlFile = useMemo(
            () => findEInvoiceFile((type) => /xml$/.test(type)),
            [eInvoice, inputFile?.url, sourceFile?.url]
        );
        const eInvoicePdf = useMemo(
            () => findEInvoiceFile((type) => type === 'application/pdf'),
            [eInvoice, inputFile?.url, sourceFile?.url]
        );

        const attachments = useMemo(() => filterAttachments(record), [record?.id]);

        const emailBodySourcePart = useMemo(() => {
            const sourceParts = record?.sourceParts || [];
            return sourceParts.find((sp: any) => sp.partType === 'BODY');
        }, [record?.sourceParts]);

        const isEmailBodySource = useMemo(() => {
            if (!emailBodySourcePart) return false;
            return record?.inputFile?.name === emailBodySourcePart.fileName;
        }, [emailBodySourcePart, record?.inputFile?.name]);

        const [selectedFileToProcess, setSelectedFileToProcess] = useState(null);
        const [createRecordFromEmailPart] = useMutation(CREATE_RECORD_FROM_EMAIL_PART);

        const handleProcessEmail = () => {
            if (emailBodySourcePart) {
                setSelectedFileToProcess({
                    name: emailBodySourcePart.fileName,
                    type: emailBodySourcePart.mimeType,
                    url: emailBodySourcePart.fileUrl,
                    __typename: 'EmailPart',
                });
            }
        };

        const handleProcessDocument = async (deleteSourceRecord: boolean, deleteReason: string | undefined) => {
            if (!selectedFileToProcess || !record) {
                return;
            }

            const sourcePart = emailBodySourcePart;

            if (!sourcePart?.id) {
                setSelectedFileToProcess(null);
                return;
            }

            try {
                const result = await createRecordFromEmailPart({
                    variables: {
                        documentType: mapDocumentTypeToGqlDocumentType[documentConfiguration.documentType],
                        recordId: record.id,
                        emailPartId: sourcePart.id,
                        deleteSourceRecord,
                        deleteReason,
                    },
                    fetchPolicy: 'no-cache',
                });

                const newRecordId = result.data?.createRecordFromEmailPart?.recordId;

                if (newRecordId) {
                    navigate(
                        url(
                            documentConfiguration.ASSISTANCE_TAB_PATH,
                            { recordId: newRecordId, tab: 'document' },
                            { keepSearch: false }
                        )
                    );
                }
            } catch (error) {
                console.error('Error processing email:', error);
            } finally {
                setSelectedFileToProcess(null);
            }
        };

        const tabs = [];
        if (emailOcr && !isInputFileEmail) {
            tabs.push(TABS_OPTIONS.EmailDocument);
        } else if (
            !sourceFile?.name &&
            sourceFile?.subject &&
            sourceFile?.attachments?.length >= 1 &&
            !isInputFileEmail
        ) {
            tabs.push(TABS_OPTIONS.Source);
        } else if (record?.voiceAgentRecord?.id) {
            tabs.push(TABS_OPTIONS.VoiceConversation);
        }
        tabs.push(TABS_OPTIONS.Document);

        // Highlight Document even if Attachment or Email are the selected tabs
        const _currentTab = useCurrentTabName();
        const currentTab = [
            TABS_OPTIONS.Source,
            TABS_OPTIONS.EmailDocument,
            TABS_OPTIONS.Document,
            TABS_OPTIONS.VoiceConversation,
        ].includes(_currentTab)
            ? _currentTab
            : TABS_OPTIONS.Document;

        if (areAttachmentsVisible) {
            viewMode = TABS_OPTIONS.Attachments;
        } else {
            viewMode = viewMode || currentTab;
        }

        const secondaryPanels = [
            TABS_OPTIONS.Attachments,
            TABS_OPTIONS.Emails,
            TABS_OPTIONS.ProcessingHistory,
            TABS_OPTIONS.MasterData,
        ];
        const isSecondaryPanel = secondaryPanels.includes(viewMode);
        const wasDocumentView =
            prevViewMode === TABS_OPTIONS.Document ||
            prevViewMode === TABS_OPTIONS.Source ||
            prevViewMode === TABS_OPTIONS.EmailDocument ||
            prevViewMode === TABS_OPTIONS.VoiceConversation;
        const shouldAnimateIn = isSecondaryPanel && wasDocumentView && prevViewMode !== undefined;

        // close masterdata browser side panel when switching to other secondary panels
        useEffect(() => {
            if (isSecondaryPanel && handlers?.masterdataBrowserSidePanelVisible) {
                handlers.closeMasterdataBrowser();
            }
        }, [isSecondaryPanel, viewMode]);

        const handleClosePanel = () => {
            const recordId = record?.id;
            setTimeout(() => {
                if (!recordId) return;
                navigate(
                    url(
                        documentConfiguration.ASSISTANCE_TAB_PATH,
                        { recordId, tab: currentTab },
                        { keepSearch: true, excludeSearch: ['mode'] }
                    )
                );
            }, 300);
        };
        const viewModeComponent = useMemo(() => {
            if (viewMode === TABS_OPTIONS.Attachments) {
                return <AssistanceSidebarAttachments attachments={attachments} onClose={handleClosePanel} />;
            } else if (viewMode === TABS_OPTIONS.Emails) {
                return <AssistanceSidebarEmail onClose={handleClosePanel} containerRef={animationContainerRef} />;
            } else if (viewMode === TABS_OPTIONS.ProcessingHistory) {
                return (
                    <AssistanceSidebarProcessingHistory
                        record={record}
                        documentConfig={documentConfiguration}
                        handlers={{ onRetryStep }}
                        onClose={handleClosePanel}
                    />
                );
            } else if (viewMode === TABS_OPTIONS.MasterData) {
                return <AssistanceSidebarMasterData record={record} onClose={handleClosePanel} />;
            } else return undefined;
        }, [viewMode, currentTab, attachments, documentConfiguration]);

        const emailHeader = useMemo(() => {
            if (
                // normal case when an attachment of an email was processed (either backfilling enabled or not)
                ((viewMode === TABS_OPTIONS.EmailDocument || viewMode === TABS_OPTIONS.Source) &&
                    isInstanceOfEmailResult(sourceFile)) ||
                // case when the email didn't contain attachments and the inputFile is the email itself
                (viewMode === TABS_OPTIONS.Document && isInputFileEmail)
            ) {
                return (
                    <div
                        ref={handleEmailHeaderRef}
                        className="absolute top-0 w-full left-0 z-20 bg-surface-medium/80 backdrop-blur p-4"
                    >
                        <div className="flex gap-3">
                            <EmailAvatar emailAddress={sourceFile.sender} />
                            <div className="flex flex-col gap-4 flex-1">
                                <EmailHeader
                                    hasSmartInbox={
                                        record.createdSource === 'SMART_INBOX' ||
                                        record.createdSource === 'SMART_INBOX_MANUAL_CREATION'
                                    }
                                    email={sourceFile}
                                    emailBodySourcePart={emailBodySourcePart}
                                    isEmailBodySource={isEmailBodySource}
                                    onProcessEmail={handleProcessEmail}
                                    onGoToDocumentClick={(recordId) => {
                                        const targetUrl = url(
                                            documentConfiguration.ASSISTANCE_TAB_PATH,
                                            { recordId, tab: 'document' },
                                            { keepSearch: false }
                                        );

                                        window.open(targetUrl, '_blank', 'noopener,noreferrer');
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                );
            }

            return null;
        }, [
            sourceFile,
            isInputFileEmail,
            viewMode,
            emailBodySourcePart,
            isEmailBodySource,
            handleProcessEmail,
            navigate,
            documentConfiguration,
            t,
            record,
        ]);

        const imageMapProps = {
            formsRef,
            previewRef,
            items,
            fields: imageMapFields,
        };

        const getImageMapConfig = (isEmailDocument: boolean) => ({
            ocr: isEmailDocument ? emailOcr : ocr,
            isEmailOcr: isEmailDocument,
            spacingTop: isEmailDocument ? 150 : emailHeader ? 150 : 100,
        });

        const renderNoDocument = () => (
            <div className="flex flex-col items-center justify-center h-full w-full max-h-full">
                <div className="flex flex-col items-center max-w-[25rem]">
                    <div className="text-sm font-semibold">{t('noDocumentSection.title')}</div>
                    {documentTabFileUrl && (
                        <>
                            <div className="mt-2 text-sm text-content-strong text-center">
                                {t('noDocumentSection.description')}
                            </div>
                            <a
                                href={documentTabFileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classnames(
                                    'block box-border p-3 mt-6 w-full',
                                    'rounded-md border border-line-strong',
                                    'text-content-strong font-medium',
                                    'shadow-sm bg-surface-weak'
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <FileIcon />
                                    <span className="flex-grow text-sm truncate">{documentTabFileName}</span>
                                    <ExternalLinkIcon />
                                </div>
                            </a>
                        </>
                    )}
                </div>
            </div>
        );

        const renderAssistanceImageMap = (isEmailDocument: boolean) => (
            <AssistanceImageMap {...imageMapProps} {...getImageMapConfig(isEmailDocument)} />
        );

        const documentSourceView = useMemo(() => {
            switch (viewMode) {
                case TABS_OPTIONS.Source:
                    return sourceFile == null ? (
                        renderNoDocument()
                    ) : (
                        <SourceFileView file={sourceFile} readOnly={readOnly} loading={isDocumentLoading} />
                    );

                case TABS_OPTIONS.VoiceConversation:
                    return <VoiceConversationView record={record} loading={isDocumentLoading} />;

                case TABS_OPTIONS.EmailDocument:
                    return renderAssistanceImageMap(true);

                default:
                    if (eInvoice) {
                        // A hybrid ZUGFeRD PDF is shown as the PDF itself: the OCR boxes would highlight text that
                        // played no part in the extraction. A pure XML invoice has no page at all, so the parsed
                        // source is rendered as an invoice instead.
                        if (eInvoicePdf) {
                            return (
                                <InputDataPreview data={eInvoicePdf} className="h-full max-h-full bg-surface-medium" />
                            );
                        }
                        return <EInvoiceView eInvoice={eInvoice} xmlFile={eInvoiceXmlFile} />;
                    }
                    return documentPreviewNotAvailable ? renderNoDocument() : renderAssistanceImageMap(false);
            }
        }, [viewMode, items, imageMapFields, eInvoice, eInvoicePdf, eInvoiceXmlFile]);

        return (
            <AssistanceViewContextProvider
                onUpdate={onUpdate}
                onReselect={onReselect}
                onFastTrackChange={setIsFastTrackEnabled}
                isFastTrackEnabled={isFastTrackEnabled}
                generalConfig={generalConfig}
                formsRef={formsRef}
                previewRef={previewRef}
                itemVirtualizerRef={itemVirtualizerRef}
            >
                <SplitPanes initialWidths={[66, 34]} initialHeights={[66, 34]}>
                    <Pane id="document-left-pane" className="bg-surface-medium overflow-hidden">
                        {isDocumentLoading ? (
                            <div
                                className={classnames(
                                    'flex flex-col items-center justify-center h-full w-full max-h-full gap-3'
                                )}
                            >
                                <LoadingIcon className="text-content-brand-strong text-sm animate-spin w-[2.5rem] h-[2.5rem]" />

                                {(!isSupported || !isPending) && (
                                    <span className="text-sm text-content-strong">{t('loadingFile')}</span>
                                )}

                                {isPending && (
                                    <span className="text-sm text-content-strong">{t('pendingFileProcessing')}</span>
                                )}

                                {isPending && isSupported && (
                                    <AssistanceNotificationsCard
                                        record={record}
                                        documentType={
                                            mapDocumentTypeToGqlDocumentType[documentConfiguration.documentType]
                                        }
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col h-full">
                                {tabs.length > 1 && (
                                    <div className="flex flex-row border-b border-solid border-line-weak bg-surface-weak">
                                        {tabs.map((tab) => (
                                            <Link
                                                className={classnames(
                                                    'flex items-center justify-center px-3 gap-2 font-medium text-sm text-content-strong transition-colors flex-1 h-14',
                                                    'border-b-2 border-solid border-line-transparent',
                                                    'force-line-height truncate',
                                                    'hover:border-line-hover hover:text-content-strong',
                                                    tab == currentTab &&
                                                        '!border-line-brand-focus !text-content-brand-strong'
                                                )}
                                                key={tab}
                                                to={url(
                                                    documentConfiguration.ASSISTANCE_TAB_PATH,
                                                    { recordId: record?.id, tab: tab },
                                                    { keepSearch: true, excludeSearch: ['mode'] }
                                                )}
                                            >
                                                {getTabIcon(tab as TABS_OPTIONS)}
                                                <Tooltip
                                                    className="flex-shrink min-w-0"
                                                    content={documentTabFileName}
                                                    overflowOnly
                                                >
                                                    <span className="truncate">
                                                        {tab === TABS_OPTIONS.Source ||
                                                        tab === TABS_OPTIONS.EmailDocument
                                                            ? t(`previewTabs.${camelCase(tab)}`)
                                                            : documentTabFileName}
                                                    </span>
                                                </Tooltip>
                                            </Link>
                                        ))}
                                    </div>
                                )}

                                <div className="h-full relative">
                                    {emailHeader}
                                    {documentSourceView}
                                </div>
                            </div>
                        )}
                    </Pane>
                    <Pane id="document-right-pane" className={viewMode !== TABS_OPTIONS.Document ? 'shadow-md' : ''}>
                        {isSecondaryPanel ? (
                            <div
                                ref={animationContainerRef}
                                key={viewMode}
                                className={classnames(
                                    'h-full w-full bg-surface-weak shadow-md overflow-x-hidden overflow-y-auto',
                                    shouldAnimateIn && 'animate-slide-in-from-right'
                                )}
                            >
                                {viewModeComponent}
                            </div>
                        ) : (
                            <AssistanceSidebar>
                                <div
                                    className="flex flex-col w-full min-h-0 flex-auto overflow-y-auto [scrollbar-gutter:stable] js-assistance-form"
                                    ref={formsRef}
                                >
                                    {canManageSelfServiceForms(user) &&
                                        documentConfiguration?.hasSelfServiceFormSupport &&
                                        isInputFileEmail && <SelfServiceFormBanner />}
                                    <ManualReviewBanner
                                        key={record?.id}
                                        document={document}
                                        itemFormFieldGroups={itemFormFieldGroups}
                                    />
                                    <IncompleteChecksBanner
                                        document={document}
                                        itemFormFieldGroups={itemFormFieldGroups}
                                    />
                                    <HeaderDataSection
                                        previewRef={previewRef}
                                        formFieldGroups={headerFormFieldGroups}
                                        additionalScrollOffset={emailHeaderHeight}
                                    />
                                    {Object.entries(itemFormFieldGroups).map(
                                        ([itemType, formFieldGroups]: [string, [any[], any[]]]) => (
                                            <ItemDataSection
                                                key={itemType}
                                                itemType={itemType}
                                                formFieldGroups={formFieldGroups}
                                                additionalScrollOffset={emailHeaderHeight}
                                            />
                                        )
                                    )}
                                </div>

                                <SidebarControlsComponent
                                    isFastTrackEnabled={isFastTrackEnabled}
                                    onFastTrackEnabledChange={setIsFastTrackEnabled}
                                    fastTrackCount={fastTrackCount}
                                />
                            </AssistanceSidebar>
                        )}
                    </Pane>
                </SplitPanes>
                <ProcessModal
                    title={t('processEmailModal.title')}
                    labelConfirm={t('processEmailModal.confirmButton')}
                    labelCancel={t('processDocumentModal.cancelButton')}
                    documentType={documentConfiguration.documentType}
                    visible={!!selectedFileToProcess}
                    onCancel={() => setSelectedFileToProcess(null)}
                    onConfirm={handleProcessDocument}
                >
                    <Trans i18nKey="processEmailModal.message" t={t} components={{ bold: <strong />, br: <br /> }} />
                </ProcessModal>
            </AssistanceViewContextProvider>
        );
    }
);

const getTabIcon = (tabName: TABS_OPTIONS) => {
    switch (tabName) {
        case TABS_OPTIONS.Source:
        case TABS_OPTIONS.EmailDocument:
            return <EmailBodyIcon />;
        case TABS_OPTIONS.Document:
            return <DocumentIcon />;
        case TABS_OPTIONS.VoiceConversation:
            return <MicrophoneIcon />;
        default:
            return <DocumentIcon />;
    }
};

export default DocumentView;
